import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import { PANEL_SURFACE } from "../layout.constants";
import { ModuleSearch } from "./ModuleSearch";
import {
  MOBILE_W,
  SIDEBAR_COLLAPSED_W,
  SIDEBAR_EXPANDED_W,
  SPRING,
} from "./sidebar.constants";

interface ISidebarFrameProps {
  isSidebarOpen: boolean;
  isCollapsed: boolean;
  isOverlay: boolean;
  onClose: () => void;
  /** Accessible name of the `<aside>` landmark. */
  label: string;
  /** Accessible name of the `<nav>` inside it. */
  navLabel: string;
  /** Top slot, above the search field (the main menu's space switcher). */
  header?: ReactNode;
  /** The filter field, hidden on the icon rail. */
  search: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  };
  /** The `<nav>`'s contents — the menu list. */
  children: ReactNode;
}

/**
 * The app sidebar's chrome, shared by the main menu and the settings sidebar
 * so the two are the same component apart from their menu: the in-flow card
 * that animates between expanded and icon-rail width on desktop, the
 * slide-in overlay with its backdrop below it, and the filter field. Sign-out
 * lives in the top bar's profile drawer, not here.
 *
 * The overlay behaves as a modal panel for keyboard and screen-reader users:
 * opening it moves focus into it, Escape closes it, closing returns focus to
 * whatever opened it, and while closed (slid off-screen, but still mounted so
 * it can animate) it is `inert` — out of the tab order and the accessibility
 * tree.
 */
export const SidebarFrame = ({
  isSidebarOpen,
  isCollapsed,
  isOverlay,
  onClose,
  label,
  navLabel,
  header,
  search,
  children,
}: ISidebarFrameProps) => {
  const { t } = useTranslation("common");
  const asideRef = useRef<HTMLElement>(null);
  const isOverlayOpen = isOverlay && isSidebarOpen;

  useEffect(() => {
    if (!isOverlayOpen) return;
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    asideRef.current?.focus({ preventScroll: true });
    return () => {
      // Closing makes the panel inert, which drops focus that was inside it
      // to <body>; hand it back to the control that opened the panel.
      const focused = document.activeElement;
      if (
        !focused ||
        focused === document.body ||
        asideRef.current?.contains(focused)
      ) {
        opener?.focus({ preventScroll: true });
      }
    };
  }, [isOverlayOpen]);

  const handleOverlayKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (isOverlayOpen && e.key === "Escape") onClose();
  };

  return (
    <TooltipProvider delayDuration={200}>
      {/* ── Overlay backdrop (mobile + tablet) ──────────────── */}
      <AnimatePresence>
        {isOverlay && isSidebarOpen && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            role="button"
            aria-label={t("header.close_sidebar")}
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => e.key === "Escape" && onClose()}
            className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* ── Sidebar shell ────────────────────────────────────── */}
      <motion.aside
        ref={asideRef}
        aria-label={label}
        // Programmatic focus target on open; never in the tab order itself.
        tabIndex={isOverlay ? -1 : undefined}
        inert={isOverlay && !isSidebarOpen}
        onKeyDown={handleOverlayKeyDown}
        initial={false}
        animate={
          isOverlay
            ? { x: isSidebarOpen ? 0 : "-100%" }
            : {
                width: isSidebarOpen ? SIDEBAR_EXPANDED_W : SIDEBAR_COLLAPSED_W,
              }
        }
        transition={SPRING}
        style={isOverlay ? { width: MOBILE_W } : undefined}
        // `scrollbar-hover-scope`: the menu list's scrollbar shows while the
        // pointer is anywhere in the sidebar, and hides when it leaves.
        className={cn(
          "scrollbar-hover-scope flex flex-col bg-white overflow-hidden outline-hidden",
          isOverlay
            ? cn("fixed inset-y-0 start-0 z-40", isSidebarOpen && "shadow-2xl")
            : cn("relative shrink-0 h-full", PANEL_SURFACE),
        )}
      >
        {/* Logo + sidebar toggle now live in the full-width top bar (see Header) */}

        {header}

        {!isCollapsed && (
          <ModuleSearch
            value={search.value}
            onChange={search.onChange}
            placeholder={search.placeholder}
            onKeyDown={search.onKeyDown}
          />
        )}

        <nav
          className="scrollbar-on-hover flex-1 overflow-y-auto overflow-x-hidden"
          aria-label={navLabel}
        >
          {children}
        </nav>
      </motion.aside>
    </TooltipProvider>
  );
};
